import * as stylex from '@stylexjs/stylex';

export const videoPageStyles = stylex.create({
    /* Model Page Base */
    modelPage: {
        maxWidth: 1200,
        margin: '0 auto',
        padding: 'var(--spacing-lg)',
        color: 'var(--text-primary)',
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--spacing-md)',
        transition: 'color var(--transition-fast)',
        ':hover': {
            color: 'var(--primary)',
        },
    },
    pageTitle: {
        fontSize: '2.5rem',
        fontWeight: 700,
        margin: 0,
        color: 'var(--text-primary)',
        '@media (max-width: 768px)': {
            fontSize: '2rem',
        },
        '@media (max-width: 480px)': {
            fontSize: '1.75rem',
        },
    },

    /* Hero Section Base */
    heroSection: {
        position: 'relative',
        height: 600,
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-3xl)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        '@media (max-width: 768px)': {
            height: 400,
            minHeight: 400,
        },
        '@media (max-width: 480px)': {
            height: 350,
            minHeight: 350,
        },
    },
    heroBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
    },
    heroBgImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center right',
    },
    heroBgVideo: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center right',
    },
    heroOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.3) 70%, transparent 100%)',
    },
    muteIndicator: {
        position: 'absolute',
        bottom: 20,
        right: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '50%',
        padding: 12,
        color: 'white',
        opacity: 0.7,
        transition: 'opacity 0.3s ease, transform 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    muteIndicatorHover: {
        opacity: 1,
        transform: 'scale(1.1)',
    },
    heroContent: {
        position: 'relative',
        zIndex: 2,
        padding: 'var(--spacing-2xl)',
        maxWidth: 600,
        color: 'white',
        '@media (max-width: 768px)': {
            padding: 'var(--spacing-md)',
            textAlign: 'left',
        },
        '@media (max-width: 480px)': {
            padding: 'var(--spacing-sm)',
        },
    },
    heroTitle: {
        fontSize: '3rem',
        fontWeight: 800,
        margin: '0 0 var(--spacing-md) 0',
        color: 'white',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        '@media (max-width: 768px)': {
            fontSize: '2.5rem',
            textAlign: 'left',
            lineHeight: 1.2,
        },
        '@media (max-width: 480px)': {
            fontSize: '1.75rem',
            lineHeight: 1.1,
        },
    },
    heroDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'rgba(255, 255, 255, 0.9)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        '@media (max-width: 768px)': {
            fontSize: '1rem',
            lineHeight: 1.5,
            marginBottom: 'var(--spacing-lg)',
        },
        '@media (max-width: 480px)': {
            fontSize: '0.9rem',
            lineHeight: 1.4,
        },
    },
    heroStats: {
        display: 'flex',
        gap: 'var(--spacing-lg)',
        flexWrap: 'wrap',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
            alignItems: 'flex-start',
        },
        '@media (max-width: 480px)': {
            gap: 'var(--spacing-sm)',
        },
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-sm)',
        color: 'rgba(255, 255, 255, 0.9)',
        fontWeight: 500,
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        '@media (max-width: 768px)': {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            minWidth: 200,
        },
        '@media (max-width: 480px)': {
            minWidth: 180,
            fontSize: '0.9rem',
        },
    },
    statSvg: {
        color: 'var(--accent)',
        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))',
    },
    creditCostBadge: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text-on-accent)',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginTop: 'var(--spacing-md)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        display: 'inline-block',
        '@media (max-width: 768px)': {
            marginTop: 'var(--spacing-lg)',
            fontSize: '0.9rem',
        },
        '@media (max-width: 480px)': {
            fontSize: '0.8rem',
            padding: 'var(--spacing-xs) var(--spacing-sm)',
        },
    },

    /* Section Titles */
    sectionTitle: {
        fontSize: '2rem',
        fontWeight: 700,
        margin: '0 0 var(--spacing-xl) 0',
        color: 'var(--text-primary)',
        position: 'relative',
        '::after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 60,
            height: 3,
            backgroundImage: 'linear-gradient(90deg, var(--primary), var(--accent))',
            borderRadius: 'var(--radius-full)',
        },
    },

    /* Important Note */
    importantNote: {
        backgroundImage: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1))',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'rgba(59, 130, 246, 0.2)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-xl)',
        marginBottom: 'var(--spacing-xl)',
        display: 'flex',
        gap: 'var(--spacing-lg)',
        alignItems: 'flex-start',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
            padding: 'var(--spacing-lg)',
        },
        '@media (max-width: 480px)': {
            padding: 'var(--spacing-md)',
            gap: 'var(--spacing-sm)',
        },
    },
    noteIcon: {
        fontSize: '1.5rem',
        flexShrink: 0,
        marginTop: '0.125rem',
        '@media (max-width: 768px)': {
            alignSelf: 'flex-start',
        },
    },
    noteContentH4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        margin: '0 0 var(--spacing-sm) 0',
        color: 'var(--text-primary)',
        '@media (max-width: 480px)': {
            fontSize: '1.1rem',
        },
    },
    noteContentP: {
        fontSize: 'var(--font-size-base)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        margin: 0,
        '@media (max-width: 480px)': {
            fontSize: '0.9rem',
        },
    },
    noteContentUl: {
        margin: 'var(--spacing-md) 0',
        paddingLeft: 'var(--spacing-lg)',
        color: 'var(--text-secondary)',
    },
    noteContentLi: {
        marginBottom: 'var(--spacing-xs)',
        lineHeight: 1.5,
        '@media (max-width: 480px)': {
            fontSize: '0.9rem',
        },
    },

    /* Gallery Section */
    gallerySection: {
        marginBottom: 'var(--spacing-3xl)',
    },
    galleryDescription: {
        fontSize: 'var(--font-size-lg)',
        color: 'var(--text-secondary)',
        lineHeight: 1.6,
        marginBottom: 'var(--spacing-xl)',
        textAlign: 'center',
        maxWidth: 800,
        marginLeft: 'auto',
        marginRight: 'auto',
    },

    /* Video Gallery Grid Layout */
    videoGalleryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        marginTop: 'var(--spacing-xl)',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gap: 'var(--spacing-sm)',
        },
    },
    videoGalleryItem: {
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-card)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        transition: 'all var(--transition-normal)',
        cursor: 'pointer',
    },
    featured: {
        gridColumn: 'span 2',
        gridRow: 'span 2',
        '@media (max-width: 768px)': {
            gridColumn: 'span 1',
            gridRow: 'span 1',
        },
    },
    wide: {
        gridColumn: 'span 2',
        '@media (max-width: 768px)': {
            gridColumn: 'span 1',
        },
    },
    tall: {
        gridRow: 'span 2',
        '@media (max-width: 768px)': {
            gridRow: 'span 1',
        },
    },
    videoContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    galleryVideo: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform var(--transition-normal)',
    },
    videoPrompt: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 80%, transparent 100%)',
        color: 'white',
        padding: 'var(--spacing-md)',
        transform: 'translateY(100%)',
        transition: 'transform var(--transition-normal)',
    },
    videoPromptHover: {
        transform: 'translateY(0)',
    },
    videoPromptP: {
        fontSize: '0.75rem',
        lineHeight: 1.3,
        display: 'block',
        fontWeight: 500,
    },

    /* Click indicator */
    clickIndicator: {
        position: 'absolute',
        right: 0,
        bottom: 0,
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: 'var(--spacing-xs) var(--spacing-sm)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        opacity: 0,
        transition: 'all var(--transition-normal)',
        pointerEvents: 'none',
        zIndex: 100,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    clickIndicatorCenter: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        opacity: 0,
        transition: 'all var(--transition-normal)',
        pointerEvents: 'none',
        zIndex: 100,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    indicatorActive: {
        opacity: 1,
        transform: 'translate(-50%, -50%) scale(1.1)',
    },

    /* Text Image Video List */
    textImageVideoList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-md)',
        maxWidth: 1200,
        margin: '0 auto',
    },
    refVideo: {
        width: '100%',
        maxWidth: 500,
        transition: 'all var(--transition-normal)',
        '@media (max-width: 768px)': { maxWidth: 450 },
        '@media (max-width: 600px)': { maxWidth: 350 },
        '@media (max-width: 480px)': { maxWidth: 250 },
    },
    refAudio: {
        width: '100%',
        maxWidth: 500,
        transition: 'all var(--transition-normal)',
        '@media (max-width: 768px)': { maxWidth: 450 },
        '@media (max-width: 600px)': { maxWidth: 350 },
        '@media (max-width: 480px)': { maxWidth: 250 },
    },
    videoElement: {
        maxWidth: 500,
        maxHeight: 500,
        width: '100%',
        height: 'auto',
        objectFit: 'contain',
        transition: 'all var(--transition-normal)',
        '@media (max-width: 768px)': { maxWidth: 450, maxHeight: 450 },
        '@media (max-width: 600px)': { maxWidth: 350, maxHeight: 350 },
        '@media (max-width: 480px)': { maxWidth: 250, maxHeight: 250 },
    },
    audioElement: {
        width: '100%',
        height: 32,
        outline: 'none',
        borderRadius: 'var(--radius-md)',
        '::-webkit-media-controls-panel': {
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--text-tertiary)',
        },
    },
    textImageVideoItem: {
        cursor: 'pointer',
        position: 'relative',
        height: 'auto',
        minHeight: 'fit-content',
    },
    imageVideoPair: {
        display: 'flex',
        gap: 'var(--spacing-sm)',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 480px)': {
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
        },
    },
    arrowSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 var(--spacing-sm)',
        '@media (max-width: 480px)': {
            transform: 'rotate(90deg)',
        },
    },
    arrow: {
        fontSize: '2rem',
        color: 'var(--primary)',
        fontWeight: 'bold',
        opacity: 0.8,
        transition: 'all var(--transition-normal)',
        '@media (max-width: 600px)': {
            fontSize: '1rem',
        },
    },
    arrowHover: {
        opacity: 1,
        transform: 'scale(1.1)',
        color: 'var(--accent)',
    },
    referenceImage: {
        position: 'relative',
        overflow: 'hidden',
    },
    generatedVideo: {
        position: 'relative',
        overflow: 'hidden',
    },
    comparisonImage: {
        maxWidth: 550,
        maxHeight: 550,
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        transition: 'all var(--transition-normal)',
        '@media (max-width: 768px)': {
            maxWidth: 450,
            maxHeight: 450,
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
        },
        '@media (max-width: 600px)': { maxWidth: 350, maxHeight: 350 },
        '@media (max-width: 480px)': { maxWidth: 250, maxHeight: 250 },
    },
    comparisonVideo: {
        maxWidth: 550,
        maxHeight: 550,
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        transition: 'all var(--transition-normal)',
        position: 'relative',
        left: '50%',
        transform: 'translateX(-50%)',
        '@media (max-width: 768px)': { maxWidth: 450, maxHeight: 450 },
        '@media (max-width: 600px)': { maxWidth: 350, maxHeight: 350 },
        '@media (max-width: 480px)': { maxWidth: 250, maxHeight: 250 },
    },
    imagePrompt: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.3) 80%, transparent 100%)',
        color: 'white',
        padding: 'var(--spacing-sm)',
        transform: 'translateY(100%)',
        transition: 'transform var(--transition-normal)',
        zIndex: 20,
        opacity: 0,
    },
    imagePromptHover: {
        transform: 'translateY(0)',
        opacity: 1,
    },

    /* Multi-image layouts */
    referenceImages: {
        position: 'relative',
        width: 550,
        height: 550,
        '@media (max-width: 768px)': { maxWidth: 450, maxHeight: 450, width: 'auto', height: 'auto' },
        '@media (max-width: 480px)': { width: 250, height: 250 },
    },
    twoImagesTopLeft: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 2,
        width: 240,
        height: 240,
        '@media (max-width: 768px)': { width: 72, height: 72 },
    },
    twoImagesBottomRight: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        zIndex: 1,
        width: 240,
        height: 240,
        '@media (max-width: 768px)': { width: 72, height: 72 },
    },
    fourImagesBase: {
        position: 'absolute',
        width: 160,
        height: 160,
        '@media (max-width: 768px)': { width: 48, height: 48 },
    },
    topLeft: { top: 0, left: 0, zIndex: 4 },
    topRight: { top: 64, left: 144, zIndex: 3 },
    bottomLeft: { bottom: 64, left: 16, zIndex: 2 },
    bottomRight: { bottom: 0, right: 64, zIndex: 1 },

    referenceImageImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 'var(--radius-md)',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        transition: 'all var(--transition-normal)',
        ':hover': {
            transform: 'scale(1.05)',
            zIndex: 10,
        },
    },

    /* Reference Image and Video Layout */
    referenceImageAndVideo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-sm)',
        alignItems: 'center',
        justifyContent: 'center',
        width: 400,
        height: 400,
        '@media (max-width: 768px)': { maxWidth: 140, maxHeight: 140, width: 'auto', height: 'auto', gap: 'var(--spacing-xs)' },
        '@media (max-width: 600px)': { maxWidth: 100, maxHeight: 100 },
    },
    referenceVideo: {
        position: 'relative',
        overflow: 'hidden',
    },
    referenceChildBox: {
        width: '100%',
        height: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
});